import React, { useState, useRef } from 'react'
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  useToast,
  Flex,
  Badge,
  Divider,
  useColorModeValue,
  Card,
  CardBody,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Textarea,
  Icon
} from '@chakra-ui/react'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { format } from 'date-fns'
import { 
  FiDownload, 
  FiShare2, 
  FiCopy, 
  FiAward,
  FiStar,
  FiShield,
  FiZap
} from 'react-icons/fi'
import html2canvas from 'html2canvas'

const Certificate = () => {
  const [auth] = useRecoilState(authState)
  const { t } = useTranslation()
  const toast = useToast()
  const certificateRef = useRef<HTMLDivElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [shareText, setShareText] = useState('')
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const isValidFuture = (iso?: string | null) => {
    if (!iso) return false
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return false
    return t > Date.now()
  }

  const getGradeInfo = (auth: any) => {
    if (isValidFuture(auth?.subscriptionAt)) {
      return {
        grade: 'SECRET',
        color: 'cyan',
        bgGradient: 'linear(to-br, brand.400, brand.600)',
        icon: FiZap,
        title: 'SECRET 등급 인증서',
        description: '최고 등급의 특별한 인증서입니다',
        benefits: [
          '우선 고객 지원',
          '커뮤니티 우선 참여',
          '레퍼럴 수익 분배',
          '구독료 수익 분배',
          '특별 이벤트 참여 자격',
        ]
      }
    } else if (isValidFuture(auth?.referralAt)) {
      return {
        grade: 'VIP',
        color: 'yellow',
        bgGradient: 'linear(to-br, yellow.400, brand.500)',
        icon: FiStar,
        title: 'VIP 등급 인증서',
        description: '특별한 VIP 등급 인증서입니다',
        benefits: [
          '우선 고객 지원',
          '커뮤니티 우선 참여',
          '레퍼럴 수익 분배',
        ]
      }
    }
    return null
  }

  const gradeInfo = getGradeInfo(auth)

  if (!gradeInfo) {
    return (
      <DefaultLayout>
        <Container maxW="container.md" py={8}>
          <VStack spacing={6} textAlign="center">
            <FiAward size={64} color="gray" />
            <Text fontSize="xl" fontWeight="bold" color="gray.500">
              {t('인증서를 받을 수 있는 등급이 아닙니다')}
            </Text>
            <Text color="gray.400">
              {t('SECRET 또는 VIP 등급이 되어야 인증서를 받을 수 있습니다')}
            </Text>
          </VStack>
        </Container>
      </DefaultLayout>
    )
  }

  const handleDownload = async () => {
    if (!certificateRef.current) return

    try {
      const canvas = await html2canvas(certificateRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      })
      
      const link = document.createElement('a')
      link.download = `WinningBot_${gradeInfo.grade}_Certificate_${auth.username}.png`
      link.href = canvas.toDataURL()
      link.click()
      
      toast({
        title: '인증서 다운로드 완료',
        description: '인증서가 성공적으로 다운로드되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: '다운로드 실패',
        description: '인증서 다운로드 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const handleShare = () => {
    const shareUrl = window.location.origin
    const shareContent = `🎉 WinningBot ${gradeInfo.grade} 등급 인증서를 받았습니다!\n\n사용자: ${auth.username}\n등급: ${gradeInfo.grade}\n\nWinningBot에서 자동매매를 시작해보세요!\n${shareUrl}`
    
    setShareText(shareContent)
    onOpen()
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText)
      toast({
        title: '복사 완료',
        description: '공유 텍스트가 클립보드에 복사되었습니다.',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: '복사 실패',
        description: '클립보드 복사에 실패했습니다.',
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }

  return (
    <DefaultLayout>
      <Box py={8}>
        <Container maxW="container.lg">
          <VStack spacing={8}>
            {/* 헤더 */}
            <VStack spacing={4} textAlign="center">
              <HStack spacing={2}>
                <Box as={gradeInfo.icon} size={32} color={gradeInfo.color === 'cyan' ? '#00BCD4' : '#D69E2E'} />
                <Text fontSize="3xl" fontWeight="bold" color={gradeInfo.color === 'cyan' ? 'brand.400' : 'yellow.400'}>
                  {gradeInfo.title}
                </Text>
              </HStack>
              <Text fontSize="lg" color="gray.400">
                {gradeInfo.description}
              </Text>
            </VStack>

          {/* 인증서 */}
          <Card
            ref={certificateRef}
            maxW="800px"
            w="full"
            bg="dark.card"
            boxShadow="0 0 30px rgba(0, 0, 0, 0.5)"
            borderRadius="xl"
            overflow="hidden"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <CardBody p={0}>
              {/* 인증서 헤더 */}
              <Box
                bgGradient={gradeInfo.bgGradient}
                p={8}
                textAlign="center"
                color="white"
                position="relative"
              >
                <VStack spacing={4}>
                  <HStack spacing={3}>
                    <Box as={gradeInfo.icon} size={48} />
                    <Text fontSize="4xl" fontWeight="bold">
                      {gradeInfo.grade}
                    </Text>
                  </HStack>
                  <Text fontSize="xl" opacity={0.9}>
                    등급 인증서
                  </Text>
                </VStack>
                
                {/* 장식 요소 */}
                <Box
                  position="absolute"
                  top={4}
                  right={4}
                  opacity={0.3}
                >
                  <FiShield size={32} />
                </Box>
                <Box
                  position="absolute"
                  top={4}
                  left={4}
                  opacity={0.3}
                >
                  <FiAward size={32} />
                </Box>
              </Box>

              {/* 인증서 내용 */}
              <VStack spacing={6} p={8}>
                <VStack spacing={4} textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="white">
                    축하합니다!
                  </Text>
                  <Text fontSize="lg" color="gray.400">
                    <span style={{ color: '#fff', fontWeight: 'bold' }}>{auth.username}</span>님은 WinningBot의 <span style={{ color: gradeInfo.color === 'cyan' ? '#4FD1C5' : '#ECC94B', fontWeight: 'bold' }}>{gradeInfo.grade}</span> 등급 사용자입니다
                  </Text>
                </VStack>

                <Divider borderColor="whiteAlpha.200" />

                {/* 사용자 정보 */}
                <VStack spacing={4} w="full">
                  <Text fontSize="lg" fontWeight="semibold" color="gray.300">
                    사용자 정보
                  </Text>
                  <HStack spacing={8} justify="center" flexWrap="wrap">
                    <VStack spacing={1}>
                      <Text fontSize="sm" color="gray.500">사용자명</Text>
                      <Text fontWeight="bold" color="white">{auth.username}</Text>
                    </VStack>
                    <VStack spacing={1}>
                      <Text fontSize="sm" color="gray.500">아이디</Text>
                      <Text fontWeight="bold" color="white">{auth.account}</Text>
                    </VStack>
                    <VStack spacing={1}>
                      <Text fontSize="sm" color="gray.500">등급</Text>
                      <Badge colorScheme={gradeInfo.color} fontSize="md" px={3} py={1} borderRadius="md">
                        {gradeInfo.grade}
                      </Badge>
                    </VStack>
                  </HStack>
                </VStack>

                <Divider borderColor="whiteAlpha.200" />

                {/* 혜택 정보 */}
                <VStack spacing={4} w="full">
                  <Text fontSize="lg" fontWeight="semibold" color="gray.300">
                    {gradeInfo.grade} 등급 혜택
                  </Text>
                  <VStack spacing={2} w="full">
                    {gradeInfo.benefits.map((benefit, index) => (
                      <HStack key={index} spacing={3} w="full" justify="flex-start">
                        <Box
                          w={2}
                          h={2}
                          bg={gradeInfo.color === 'cyan' ? 'brand.400' : 'yellow.400'}
                          borderRadius="full"
                          flexShrink={0}
                          mt={1}
                        />
                        <Text fontSize="sm" color="gray.400">
                          {benefit}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>

                <Divider borderColor="whiteAlpha.200" />

                {/* 발급 정보 */}
                <VStack spacing={2} textAlign="center">
                  <Text fontSize="sm" color="gray.500">
                    발급일: {format(new Date(), 'yyyy년 MM월 dd일')}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    WinningBot - 리플 전문 자동매매 플랫폼
                  </Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* 액션 버튼 */}
          <HStack spacing={4} flexWrap="wrap" justify="center">
            <Button
              leftIcon={<FiDownload />}
              colorScheme={gradeInfo.color}
              size="lg"
              onClick={handleDownload}
              boxShadow={`0 0 20px ${gradeInfo.color === 'cyan' ? 'rgba(0, 200, 255, 0.4)' : 'rgba(236, 201, 75, 0.4)'}`}
              _hover={{ transform: 'translateY(-2px)' }}
            >
              인증서 다운로드
            </Button>
            {/* <Button
              leftIcon={<FiShare2 />}
              variant="outline"
              colorScheme={gradeInfo.color}
              size="lg"
              onClick={handleShare}
            >
              공유하기
            </Button> */}
          </HStack>
          </VStack>
        </Container>

        {/* 공유 모달 */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
          <ModalOverlay backdropFilter="blur(5px)" />
          <ModalContent bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
            <ModalHeader color="white">인증서 공유하기</ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <Text fontSize="sm" color="gray.400">
                  아래 텍스트를 복사하여 SNS나 메신저에 공유하세요.
                </Text>
                <Textarea
                  value={shareText}
                  onChange={(e) => setShareText(e.target.value)}
                  rows={6}
                  resize="none"
                  fontSize="sm"
                  bg="dark.bg"
                  borderColor="whiteAlpha.200"
                  color="white"
                  _focus={{ borderColor: 'brand.500' }}
                />
                <HStack spacing={2}>
                  <Button
                    leftIcon={<FiCopy />}
                    colorScheme="blue"
                    onClick={copyToClipboard}
                  >
                    복사하기
                  </Button>
                  <Button variant="outline" onClick={onClose} color="gray.400" _hover={{ bg: 'whiteAlpha.100', color: 'white' }}>
                    닫기
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </DefaultLayout>
  )
}

export default Certificate
